// Turning a phone camera file into something worth uploading.
//
// A photo straight off a modern phone is 3-12 MB and 4000px wide; the biggest
// thing it is ever painted into here is a lightbox. Downscaling in the browser
// before the upload is what keeps a submission inside a serverless request body
// (and off a student's data plan) — so it happens here, not on the server, and
// the server's size cap is only the backstop.
//
// HEIC is the case that makes this fiddly. Every iPhone shoots it by default;
// iOS hands the file input a JPEG most of the time, but not always (picking out
// of Files rather than Photos gives you the real thing). Safari can decode HEIC
// because the OS can — no other browser can, at all. So: try to decode and
// downscale, and if the browser simply cannot read the format, send the
// original bytes rather than losing the photo. Adding a HEIC decoder would mean
// a WASM dependency inside a folder whose whole contract is "react and
// @react-google-maps/api only".
//
// Copy-out clean: no imports outside src/pathfinder.

const MAX_EDGE = 1600;   // long edge, px — plenty for the lightbox
const QUALITY = 0.72;    // JPEG; visually clean at this size, ~1/20th the bytes
export const MAX_PHOTOS = 4;
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024; // refuse absurd input outright

// A format we couldn't downscale gets uploaded as-is, so it has to fit in one
// serverless request on its own (the platform cap is 4.5 MB, and base64 costs
// a third on top). Photos are sent one per request precisely so this is the
// only thing that has to fit.
const MAX_PASSTHROUGH_BYTES = 3 * 1024 * 1024;

const isHeic = (file) =>
  /image\/hei[cf]/i.test(file?.type || '') || /\.hei[cf]$/i.test(file?.name || '');

/** Decode a File, honouring EXIF orientation where the browser can. */
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); }
    catch { /* Safari < 17 rejects the option — fall through */ }
    try { return await createImageBitmap(file); } catch { /* fall through */ }
  }
  // <img> decoding: browsers apply EXIF orientation here on their own.
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url;
    });
  } finally {
    // Revoking immediately is safe: the decoded image keeps its own data.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/** The whole file, base64'd, unchanged. The fallback when we can't re-encode. */
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(new Error('Could not read that file.'));
    fr.readAsDataURL(file);
  });
}

/**
 * One image File → a data URL ready to POST: a downscaled JPEG whenever the
 * browser can decode the format, otherwise the original bytes.
 * Throws with a message meant for the user if the file isn't usable at all.
 */
export async function compressImage(file) {
  if (!file) throw new Error('No file selected.');
  // Type sniffing is unreliable for HEIC — some pickers report an empty type
  // and some report image/heic — so the extension gets a vote too. Anything
  // that claims a non-image type outright is still refused.
  const type = file.type || '';
  if (type && !/^image\//i.test(type) && !isHeic(file)) throw new Error('That file is not an image.');
  if (file.size > MAX_SOURCE_BYTES) throw new Error('That photo is too big to upload.');

  let src = null;
  try { src = await decode(file); } catch { src = null; }

  if (!src || !src.width || !src.height) {
    // Almost always HEIC outside Safari. Send it whole if it will fit.
    if (file.size > MAX_PASSTHROUGH_BYTES) {
      throw new Error(
        isHeic(file)
          ? "This browser can't shrink HEIC photos, and that one is too big to send as-is. Try again from your phone, or save it as a JPEG first."
          : "Couldn't read that photo. Try a JPG or PNG."
      );
    }
    const raw = await readAsDataUrl(file);
    if (!/^data:image\//i.test(raw)) throw new Error("Couldn't read that photo. Try a JPG or PNG.");
    return raw;
  }

  const w = src.width, h = src.height;
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  // A JPEG has no alpha; without this, transparent PNG corners come out black.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(src, 0, 0, cw, ch);
  src.close?.();

  const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
  // Safari returns a stub data URL when the canvas is too big to export; the
  // original is a better outcome than an empty upload.
  if (!dataUrl.startsWith('data:image/jpeg') || dataUrl.length < 2048) {
    if (file.size <= MAX_PASSTHROUGH_BYTES) return await readAsDataUrl(file);
    throw new Error('Could not process that photo.');
  }
  return dataUrl;
}

/** Prepare a picked FileList, keeping at most MAX_PHOTOS. Rejects on the first bad file. */
export async function compressAll(files) {
  const list = Array.from(files || []).slice(0, MAX_PHOTOS);
  const out = [];
  for (const f of list) out.push(await compressImage(f));
  return out;
}
